import {
  PROFILE_COVER_HEIGHT,
  PROFILE_COVER_WIDTH,
} from 'helpers/profileCoverAspect';

/**
 * Curated in-house cover presets.
 *
 * The profile header applies a 40% black inset scrim, so every preset is
 * painted brighter than it appears: paint = target / 0.6 per channel. The
 * names and the "target" look are the post-scrim result. Keep each target
 * channel <= 153 (0x99) or it can't be reached through the scrim.
 *
 * `paint(ctx, w, h)` draws the preset into a canvas at the exact header ratio.
 * `css` mirrors the paint for tiles / the auto-assigned placeholder.
 */
export const PROFILE_COVER_PRESETS = [
  {
    id: 'harbour',
    name: 'Harbour',
    css: 'linear-gradient(135deg, #2d4666 0%, #4673a2 45%, #66b1ee 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#2d4666');
      g.addColorStop(0.45, '#4673a2');
      g.addColorStop(1, '#66b1ee');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.04);
    },
  },
  {
    id: 'moss',
    name: 'Moss',
    css: 'linear-gradient(160deg, #4d614e 0%, #7b9e73 50%, #cbfdb2 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w * 0.2, h);
      g.addColorStop(0, '#4d614e');
      g.addColorStop(0.5, '#7b9e73');
      g.addColorStop(1, '#cbfdb2');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.035);
    },
  },
  {
    id: 'dusk',
    name: 'Dusk',
    css: 'linear-gradient(45deg, #614d67 0%, #9d73a0 55%, #fcb2e3 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, h, w, 0);
      g.addColorStop(0, '#614d67');
      g.addColorStop(0.55, '#9d73a0');
      g.addColorStop(1, '#fcb2e3');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.04);
    },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    css: 'linear-gradient(180deg, #3a4148 0%, #616b76 60%, #96a7bb 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#3a4148');
      g.addColorStop(0.6, '#616b76');
      g.addColorStop(1, '#96a7bb');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.045);
    },
  },
  {
    id: 'amber',
    name: 'Amber',
    css: 'linear-gradient(135deg, #6b4e39 0%, #aa844b 50%, #eecb66 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#6b4e39');
      g.addColorStop(0.5, '#aa844b');
      g.addColorStop(1, '#eecb66');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintGlow(ctx, w, h, '236, 178, 45', 0.18);
      paintNoise(ctx, w, h, 0.03);
    },
  },
  {
    id: 'reef',
    name: 'Reef',
    css: 'linear-gradient(150deg, #1e5066 0%, #3a96a0 50%, #66eee6 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w * 0.2, h);
      g.addColorStop(0, '#1e5066');
      g.addColorStop(0.5, '#3a96a0');
      g.addColorStop(1, '#66eee6');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.035);
    },
  },
  {
    id: 'current',
    name: 'Current',
    css: 'linear-gradient(110deg, #2b435d 0%, #2669b1 50%, #1184ff 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, h * 0.2, w, h * 0.8);
      g.addColorStop(0, '#2b435d');
      g.addColorStop(0.5, '#2669b1');
      g.addColorStop(1, '#1184ff');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.04);
    },
  },
  {
    id: 'copper',
    name: 'Copper',
    css: 'linear-gradient(135deg, #612b11 0%, #b7571b 50%, #ff8a2f 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#612b11');
      g.addColorStop(0.5, '#b7571b');
      g.addColorStop(1, '#ff8a2f');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintGlow(ctx, w, h, '255, 140, 40', 0.16);
      paintNoise(ctx, w, h, 0.035);
    },
  },
  {
    id: 'garnet',
    name: 'Garnet',
    css: 'linear-gradient(135deg, #4d1421 0%, #992139 50%, #ff3450 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#4d1421');
      g.addColorStop(0.5, '#992139');
      g.addColorStop(1, '#ff3450');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintGlow(ctx, w, h, '255, 60, 70', 0.12);
      paintNoise(ctx, w, h, 0.035);
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    css: 'linear-gradient(135deg, #61213f 0%, #b7467b 50%, #ff6eb7 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#61213f');
      g.addColorStop(0.5, '#b7467b');
      g.addColorStop(1, '#ff6eb7');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintGlow(ctx, w, h, '255, 110, 170', 0.14);
      paintNoise(ctx, w, h, 0.035);
    },
  },
  {
    id: 'indigo',
    name: 'Indigo',
    css: 'linear-gradient(45deg, #2b215d 0%, #4d3caa 55%, #7b61ff 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, h, w, 0);
      g.addColorStop(0, '#2b215d');
      g.addColorStop(0.55, '#4d3caa');
      g.addColorStop(1, '#7b61ff');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintGlow(ctx, w, h, '140, 120, 255', 0.12);
      paintNoise(ctx, w, h, 0.04);
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    css: 'linear-gradient(160deg, #121925 0%, #0d121b 55%, #070a0f 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#121925');
      g.addColorStop(0.55, '#0d121b');
      g.addColorStop(1, '#070a0f');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      paintNoise(ctx, w, h, 0.05);
    },
  },
];

function paintNoise(ctx, w, h, alpha, color = '255,255,255') {
  const step = Math.max(4, Math.floor(w / 200));
  ctx.fillStyle = `rgba(${color},${alpha})`;
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      if ((x * 17 + y * 31) % 7 === 0) {
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }
}

/** Soft radial glow, upper right — gives warm presets some depth. */
function paintGlow(ctx, w, h, rgb, alpha) {
  const glow = ctx.createRadialGradient(
    w * 0.75,
    h * 0.3,
    0,
    w * 0.75,
    h * 0.3,
    w * 0.45
  );
  glow.addColorStop(0, `rgba(${rgb}, ${alpha})`);
  glow.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
}

/**
 * Stable non-negative integer from a numeric or string id (e.g. UUIDs),
 * so string ids don't all collapse to the first preset.
 */
function stableIndexSeed(userId) {
  const n = Number(userId);
  if (Number.isFinite(n)) {
    return Math.abs(Math.trunc(n));
  }
  const s = String(userId || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Deterministic default preset from a user id (never ships empty banner).
 * @param {number|string} userId
 * @returns {typeof PROFILE_COVER_PRESETS[0]}
 */
export function getDefaultCoverPreset(userId) {
  const index = stableIndexSeed(userId) % PROFILE_COVER_PRESETS.length;
  return PROFILE_COVER_PRESETS[index];
}

export function getCoverPresetById(presetId) {
  return (
    PROFILE_COVER_PRESETS.find((p) => p.id === presetId) ||
    PROFILE_COVER_PRESETS[0]
  );
}

/**
 * Render a preset to a JPEG File at the exact cover thumbnail resolution.
 * Presets skip the crop step — the file is already framed.
 */
export function renderCoverPresetToFile(preset) {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Canvas unavailable'));
  }

  const canvas = document.createElement('canvas');
  canvas.width = PROFILE_COVER_WIDTH;
  canvas.height = PROFILE_COVER_HEIGHT;
  const ctx = canvas.getContext('2d');
  preset.paint(ctx, PROFILE_COVER_WIDTH, PROFILE_COVER_HEIGHT);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to export cover preset'));
          return;
        }
        const file = new File([blob], `cover-${preset.id}.jpg`, {
          type: 'image/jpeg',
        });
        resolve(file);
      },
      'image/jpeg',
      0.92
    );
  });
}

const COVER_SOURCE_KEY = 'twine.profileCoverSource';

/**
 * Persist whether the cover was auto-assigned, a chosen preset, or an upload.
 * Auto-assigned stays client-side CSS only (placeholder) and does not count
 * toward the profile-score checklist.
 */
export function getCoverSource(userId) {
  if (typeof window === 'undefined' || !userId) {
    return { type: 'auto' };
  }
  try {
    const raw = window.localStorage.getItem(`${COVER_SOURCE_KEY}.${userId}`);
    if (!raw) {
      return { type: 'auto' };
    }
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.type === 'preset' || parsed.type === 'upload')) {
      return parsed;
    }
  } catch (err) {
    // ignore
  }
  return { type: 'auto' };
}

export function setCoverSource(userId, source) {
  if (typeof window === 'undefined' || !userId) {
    return;
  }
  try {
    if (!source || source.type === 'auto') {
      window.localStorage.removeItem(`${COVER_SOURCE_KEY}.${userId}`);
      return;
    }
    window.localStorage.setItem(
      `${COVER_SOURCE_KEY}.${userId}`,
      JSON.stringify(source)
    );
  } catch (err) {
    // ignore
  }
}
